import {
  Flex,
  HStack,
  Portal,
  Text,
  useBreakpointValue
} from '@chakra-ui/react'
import { MoreVertDots } from '@components/icons'
import { useExperienceManager } from '@hooks'
import { BlueBLogo } from '@images'
import { formatTimeFromSeconds } from '@utilities'
import React, { useState } from 'react'
import {
  DailyCameraButton,
  DailyMicButton,
  DailyShareScreenButton,
  EndSessionButton,
  ParticipantsButton,
  TelehealthAssistButton,
  TelehealthSettingsButton
} from '../../footer'
import { useTelehealthRecordingTimer } from '../context/active-telehealth-session.context'
import { useClinicianDailyManager } from '../context/clinician-daily-manager.context'
import { DeviceSettingsModal } from './device-settings-modal'

type FooterPropsT = {
  onSessionEnd: () => void
  client: any
  activeSidebarTab: string
  toggleSidebar: (tab: 'assist' | 'participants' | 'none') => void
}

export const ClinicianTelehealthFooter: React.FC<FooterPropsT> = React.memo(
  ({ onSessionEnd, client, activeSidebarTab, toggleSidebar }) => {
    const { showAssist } = useExperienceManager()
    const isMobile = useBreakpointValue({ base: true, sm: false })

    const { isInCall } = useClinicianDailyManager()
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)

    return (
      <Portal>
        <Flex
          position="fixed"
          bottom="0"
          h="60px"
          w="100%"
          bg="white"
          justify={{ base: 'center', sm: 'space-between' }}
          align="center"
          px="small"
          zIndex={2}
        >
          <Flex
            display={{ base: 'none', sm: 'flex' }}
            h="100%"
            justify="center"
            align="center"
            minWidth="170px"
            maxWidth="300px"
            pl="xsmall"
          >
            <BlueBLogo mr="16px" w="19px" h="24px" />
            {client && (
              <Text
                ml="xxsmall"
                alignSelf="center"
                fontSize="14px"
                fontWeight="bold"
              >
                {`${client?.first_name} ${client?.last_name}  `}
              </Text>
            )}
            <DurationTimer />
          </Flex>
          <HStack gap="0px">
            {isInCall && (
              <>
                <DailyCameraButton />
                <DailyMicButton />
                {!isMobile && <DailyShareScreenButton />}
                <TelehealthSettingsButton
                  onClick={() => setIsSettingsOpen(true)}
                  icon={<MoreVertDots />}
                />

                <EndSessionButton onClick={onSessionEnd} />
              </>
            )}
          </HStack>
          <Flex h="100%" w="fit-content" justify="flex-end" align="center">
            <ParticipantsButton
              isActive={activeSidebarTab === 'participants'}
              onClick={() => {
                toggleSidebar('participants')
              }}
            />
            {true && (
              <TelehealthAssistButton
                isActive={activeSidebarTab === 'assist'}
                onClick={() => {
                  toggleSidebar('assist')
                }}
              />
            )}
          </Flex>
        </Flex>
        <DeviceSettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </Portal>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.activeSidebarTab === nextProps.activeSidebarTab &&
      prevProps.toggleSidebar === nextProps.toggleSidebar
    )
  }
)

const DurationTimer = () => {
  const { recordingDuration } = useTelehealthRecordingTimer()

  return (
    <Text
      pt="3px"
      ml="small"
      as="span"
      fontSize="12px"
      sx={{
        color: 'dark_gray',
        fontVariantNumeric: 'oldstyle-nums tabular-nums'
      }}
    >
      {formatTimeFromSeconds(recordingDuration)}
    </Text>
  )
}
